import "./scrollable.css";
import "./search-page.scss";
import { Page, PageHeader } from "@nike/epic-react-ui";
import { Consignment, Exception, EquipmentVehicle, User } from "../graphql/graphql";
import { GLOBAL_SEARCH } from "../graphql/globalsearch.graphql";
import { GlobalSearch } from "../shared/GlobalSearch";
import { useQuery } from "@apollo/client";
import {
    CONSIGNMENT_RESULT_TRANSFORM,
    EXCEPTION_RESULT_TRANSFORM,
    GlobalSearchPanel,
    ResultsWithCount,
    VEHICLE_RESULT_TRANSFORM
} from "../components/global-search/GlobalSearchPanel";
import { PageSpinner } from "../shared/PageSpinner";
import React, { useRef, useEffect } from "react";

const DEFAULT_PAGE_SIZE = 10;

type SearchPageProps = {
  history: any;
  currentTerritories: string[];
  currentUser?: User | undefined;
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
  debouncedGlobalSearch: string;
};

export function SearchPage({ history,  currentTerritories, currentUser, globalSearch, setGlobalSearch, debouncedGlobalSearch }: SearchPageProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setGlobalSearch(e.target.value);
    };

    const shouldSkipQuery = !currentTerritories || currentTerritories.length === 0 || !currentUser;
    const pageSize = DEFAULT_PAGE_SIZE;

    const { loading, error, data } = useQuery(GLOBAL_SEARCH, {
      skip: shouldSkipQuery || !debouncedGlobalSearch || debouncedGlobalSearch.trim() === '',
      variables: { customsTerritories: currentTerritories, limit: pageSize, searchTerm: debouncedGlobalSearch },
    });

    const exceptionResults = data?.search?.exceptions?.items || [];
    const exceptionCount = data?.search?.exceptions?.pageInfo?.totalCount || 0;
    const vehicleResults = data?.search?.equipmentVehicles?.items || [];
    const vehicleCount = data?.search?.equipmentVehicles?.pageInfo?.totalCount || 0;
    const consignmentResults = data?.search?.consignments?.items || [];
    const consignmentCount = data?.search?.consignments?.pageInfo?.totalCount || 0;

    function toResults<T>(results: T[], count: number): ResultsWithCount<T> {
        return { results, count };
    }

    return (
        <Page
        documentTitle="Search - Customs Compliance Hub"
        width="full"
        className="scrollable search-page"
        >
        <PageHeader title="Search" />
          <div className='eds-flex eds-flex--direction-column'>
            <div className='eds-flex--align-items-center eds-spacing--mb-32'>
              <GlobalSearch
                globalSearch={globalSearch}
                handleGlobalSearchChange={handleSearchChange}
                inputRef={inputRef}
              />
            </div>

            {loading && <div><PageSpinner /></div>}
            
            {error && <div>Error: {error.message}</div>}
            
            {!loading && !error && (debouncedGlobalSearch) && (
              <div className='eds-grid eds-grid--xxl-cols-3'>
                <GlobalSearchPanel<Exception> debouncedGlobalSearch={debouncedGlobalSearch}
                                              currentTerritories={currentTerritories}
                                              customization={EXCEPTION_RESULT_TRANSFORM}
                                              firstPage={toResults(exceptionResults,exceptionCount)}
                                              pageSize={pageSize} />
                <GlobalSearchPanel<EquipmentVehicle> debouncedGlobalSearch={debouncedGlobalSearch}
                                                     currentTerritories={currentTerritories}
                                                     customization={VEHICLE_RESULT_TRANSFORM}
                                                     firstPage={toResults(vehicleResults,vehicleCount)}
                                                     pageSize={pageSize} />

                <GlobalSearchPanel<Consignment> debouncedGlobalSearch={debouncedGlobalSearch}
                                                currentTerritories={currentTerritories}
                                                customization={CONSIGNMENT_RESULT_TRANSFORM}
                                                firstPage={toResults(consignmentResults,consignmentCount)}
                                                pageSize={pageSize} />
            </div>)}
          </div>
        </Page>
    );
    }